import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
import DashboardLayout from '../layouts/dashboard';
import ProjectLayout from '../layouts/project';
import JobLayout from '../layouts/jobs';
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import { PATH_AFTER_LOGIN } from '../config';
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {

  return useRoutes([
    {
      // path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            // <GuestGuard>
            //   <Register />
            // </GuestGuard>
            <Register />
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'reset-confirm-password', element: <ResetConfirmPassword /> },
        { path: 'verify', element: <VerifyCode /> },
        { path: 'project-preview', element: <ProjectPreview /> },
      ],
    },
    // User Profile
    {
      path: '/:userId',
      children: [
        {
          path: '',
          element: <LocationDetailsPage />,
        },
      ],
    },
    // Dashboard Routes
    {
      // path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
        // <DashboardLayout />
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'profile', element: <ProfilePageMain /> },


        {
          path: 'jobs',
          children: [
            // { element: <Navigate to="/jobs/postings" replace />, index: true },
            { path: 'postings', element: <JobCreate /> },
            { path: 'create', element: <JobMain /> },
            { path: 'edit', element: <JobEdit /> },
          ],
        },
        {
          path: 'setting',
          element: <SettingPageMain />,
        },
        {
          path: 'contacts',
          element: <MyContactPageMain />,
        },
        {
          path: 'dashboard',
          element: <GeneralApp />,
        },
        {
          path: 'search',
          children: [
            { element: <Navigate to="/search/talentSearch" replace />, index: true },
            { path: 'talentSearch', element: <TalentSearchMains /> },
            { path: 'talentSearchResult/:flag/:searchParameters', element: <ListOfTalent /> },
            { path: 'talentSearchMain/:flag/:searchId/:searchParameters', element: <SavedSearchShow /> },
          ],
        },
        {
          path: 'network',
          children: [
            { path: 'followers', element: <Followers /> },
            { path: 'following', element: <Following /> },
            { path: 'mynetwork', element: <MyNetwork /> },
            { path: 'network', element: <Network /> },
            { path: 'myinvitation', element: <MyInvitation /> },
          ],
        },
        {
          path: 'project',
          children: [
            { path: 'project-details', element: <ProjectDetails /> },
            { path: 'photos', element: <ProjectPhotos /> },
            { path: 'videos', element: <ProjectVideos /> },
            { path: 'project', element: <ProjectView /> },
            { path: 'project-create', element: <ProjectCreate /> },
            { path: 'teams', element: <Teams /> },
            { path: 'createmember/:staffId/:slugName', element: <TeamMember /> },
            { path: 'project-preview', element: <ProjectPreview /> },
            { path: 'list', element: <Projects /> },
            { path: 'project-view', element: <ViewProject /> },
          ],
        },
        {
          path: 'report',
          children: [
            { element: <Navigate to="/report/activity" replace />, index: true },
            { path: 'activity', element: <Activity /> },
          ],
        },
        { path: 'calendar', element: <CalendarMain /> },
        {
          path: 'inbox',
          children: [
            { path: '', element: <Inbox /> },
            { path: 'notification', element: <Notifications /> },
            { path: 'enquiry', element: <Enquiry /> },
          ],
        },
        { path: 'website', element: <WebsiteAboutUs /> },
        // { path: 'templates', element: <Templates /> },
        // { path: 'resumes', element: <Resumes /> },
        // { path: 'coverLetter', element: <CoverLetter /> },
        { path: 'subscription', element: <Subscription /> },
        // { path: 'teams', element: <Teams /> },
      ],
    },
    {
      path: 'projects',
      element: <ProjectLayout />,
      children: [
        { path: ':projectid/dashboard', element: <ProjectDashboard /> },
        // { path: ':projectid/dashboard', element: <MainDashboard /> },
        { path: ':projectid/team_quick/:departtid', element: <QuickTemplate /> },
        { path: ':projectid/team_details/:departtid/:roletype', element: <DetailedTemplate /> },
        { path: ':projectid/team/team_quick/:departtid', element: <TeamsQuick /> },
        { path: ':projectid/team/team_details/:departtid/:roletype', element: <TeamsDetailed /> },
        { path: 'projectDashboard', element: <ProjectDashboard /> },
        { path: 'projectDepartments', element: <ProjectDepartments /> },
        { path: ':projectid/newsUpdate', element: <NewsUpdate /> },
        { path: ':projectid/projectSettings', element: <ProjectSettings /> },
        { path: ':projectid/budget', element: <Budget /> },
        { path: ':projectid/wages', element: <Wages /> },
        { path: ':projectid/timeLine', element: <TimeLine /> },
        { path: ':projectid/reports', element: <Reports /> },
        { path: ':projectid/jobs', element: <ProjectJobs /> },
        { path: ':projectid/team', element: <ProjectTeam /> },
        { path: ':projectid/remuneration', element: <Remuneration /> },
        { path: ':projectid/remuneration-detailed', element: <RemunerationDetailed /> },
        { path: ':projectid/remuneration-invoice', element: <RemunerationInvoice /> },
        { path: ':projectid/teamDashboard/:rowId/:deptID', element: <TeamDashboard /> },
        { path: ':projectid/Payments/:rowId/:deptID', element: <TeamPayment /> },
        { path: ':projectid/Connect/:rowId/:deptID', element: <TeamConnect /> },
        { path: ':projectid/teamReports/:rowId/:deptID', element: <TeamReports /> },
        { path: ':projectid/TeamSettings/:rowId/:deptID', element: <TeamSetting /> },
      ],
    },
    {
      path: 'jobs',
      element: <JobLayout />,
      children: [
        { path: 'Dashboard/:jobId', element: <ViewJobDashboard /> },
        { path: 'Candidates/:jobId', element: <ViewCandidateList /> },
        { path: 'Messages/:jobId', element: <JobDashboardMessage /> },
        { path: 'Interviews/:jobId', element: <Interviews /> },
        { path: 'Jobseek/:jobId', element: <JobseekView /> },
        { path: 'reports/:jobId', element: <JobReport /> },
        { path: 'timeline/:jobId', element: <JobTimeline /> },
        { path: 'ViewCandidate/:jobId/:candidateId', element: <ViewCandidate /> },
        { path: 'settings/:jobId', element: <JobPostingsSettings /> },
        { path: 'jobCreate', element: <JobOfficeCreate /> },
        { path: 'JobOfficePreview/:jobId', element: <JobOfficePreview /> },
        { path: 'jobProjectCreate/:projectId', element: <JobProjectCreate /> },
        { path: 'jobProjectEdit/:jobId', element: <JobProjectEdit /> },
        { path: 'jobCompanyEdit/:jobId', element: <JobCompanyEditPage /> },
        { path: 'castJobPosting/:jobId', element: <CastJobPosting /> },
        { path: 'crewJobPosting/:jobId', element: <CrewJobPosting /> },
        { path: 'jobProjectList', element: <JobProjectList /> },
      ],
    },

    {
      path: '/',
      element: <Login />,
      children: [{ element: <HomePage />, index: true }],
    },
    { path: '*', element: <Navigate to="/" replace /> },
  ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const ResetConfirmPassword = Loadable(lazy(() => import('../pages/auth/ResetConfirmPass')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));
// Dashboard
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
const PortFolio = Loadable(lazy(() => import('../pages/ComingSoon')));
// Main
const HomePage = Loadable(lazy(() => import('../pages/Home')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
const LocationDetailsPage = Loadable(lazy(() => import('../pages/dashboard/LocationDetails')));

// const Templates = Loadable(lazy(() => import('../pages/dashboard/templates/ThemeProfile')));
// const Resumes = Loadable(lazy(() => import('../pages/dashboard/templates/Resumes')));
// const CoverLetter = Loadable(lazy(() => import('../pages/dashboard/templates/CoverLetter')));

const SettingPageMain = Loadable(lazy(() => import('../pages/dashboard/SettingPage')));
const MyContactPageMain = Loadable(lazy(() => import('../pages/dashboard/MyContact')));



const CalendarMain = Loadable(lazy(() => import('../pages/dashboard/Calendar')));

const ProfilePageMain = Loadable(lazy(() => import('../pages/dashboard/EmployerPage')));
const TalentSearchMains = Loadable(lazy(() => import('../pages/dashboard/TalentSearchPage')));
const ListOfTalent = Loadable(lazy(() => import('../sections/@dashboard/user/profile/SavedTalentAdvancedSearch')));
const JobMain = Loadable(lazy(() => import('../pages/dashboard/JobCreate')));
const SavedSearchShow = Loadable(lazy(() => import('../sections/@dashboard/user/profile/SavedTalentAdvancedSearch')));
const JobCreate = Loadable(lazy(() => import('../pages/dashboard/JobsPage')));

const JobOfficeCreate = Loadable(lazy(() => import('../pages/dashboard/JobCreate')));
const JobProjectCreate = Loadable(lazy(() => import('../pages/dashboard/JobsSection.js/CreatePage')));
const JobProjectEdit = Loadable(lazy(() => import('../pages/dashboard/JobsSection.js/EditPage')));
const JobCompanyEditPage = Loadable(lazy(() => import('../pages/dashboard/EditOfficeJob')));
const JobProjectList = Loadable(lazy(() => import('../pages/dashboard/JobsSection.js/JobProjectsList')));
const JobOfficePreview = Loadable(lazy(() => import('../pages/dashboard/JobsSection.js/jobOfficePreview')));
const ViewProject = Loadable(lazy(() => import('../pages/dashboard/ProjectView')));
const ProjectCreate = Loadable(lazy(() => import('../pages/dashboard/projects/CreateProject')));
const CastJobPosting = Loadable(lazy(() => import('../pages/dashboard/JobsSection.js/CastJobPosting')));
const CrewJobPosting = Loadable(lazy(() => import('../pages/dashboard/JobsSection.js/CrewJobPosting')));

const JobEdit = Loadable(lazy(() => import('../pages/dashboard/JobsEditPage')));
const JobPostingsSettings = Loadable(lazy(() => import('../pages/dashboard/JobPostingSettings/Setting')));
const Followers = Loadable(lazy(() => import('../pages/dashboard/connectnetwork/Followers')));
const Following = Loadable(lazy(() => import('../pages/dashboard/connectnetwork/Following')));
const Network = Loadable(lazy(() => import('../pages/dashboard/connectnetwork/Network')));
const MyNetwork = Loadable(lazy(() => import('../pages/dashboard/connectnetwork/MyNetwork')));
const MyInvitation = Loadable(lazy(() => import('../pages/dashboard/connectnetwork/MyInvitation')));
const WebsiteAboutUs = Loadable(lazy(() => import('../pages/dashboard/Website/AboutUs')));
const Inbox = Loadable(lazy(() => import('../pages/dashboard/InBox/JobInbox')));
const Subscription = Loadable(lazy(() => import('../pages/dashboard/InBox/Subscription')));
const Notifications = Loadable(lazy(() => import('../pages/dashboard/InBox/Notifications')));
const Enquiry = Loadable(lazy(() => import('../pages/dashboard/InBox/Enquiry')));
const Activity = Loadable(lazy(() => import('../pages/dashboard/Reports/Activity')));

const ProjectDetails = Loadable(lazy(() => import('../pages/dashboard/UserProfileProjects')));
const ProjectPhotos = Loadable(lazy(() => import('../pages/dashboard/UserPhotosProject')));
const ProjectVideos = Loadable(lazy(() => import('../pages/dashboard/UserVideoProject')));
const ProjectView = Loadable(lazy(() => import('../pages/dashboard/ProjectAddPage')));

const ProjectPreview = Loadable(lazy(() => import('../pages/dashboard/PreviewProject')));
const MainDashboard = Loadable(lazy(() => import('../pages/dashboard/projects/MainDashboard')));

const ProjectDashboard = Loadable(lazy(() => import('../pages/dashboard/projects/ProjectDashboard')));
const ProjectDepartments = Loadable(lazy(() => import('../pages/dashboard/projects/ProjectDepartments')));
const NewsUpdate = Loadable(lazy(() => import('../pages/dashboard/projects/NewsUpdate')));
const Wages = Loadable(lazy(() => import('../pages/dashboard/projects/Wages')));
const ProjectSettings = Loadable(lazy(() => import('../pages/dashboard/projects/ProjectSettings')));
const Budget = Loadable(lazy(() => import('../pages/dashboard/projects/Budget')));
const TimeLine = Loadable(lazy(() => import('../pages/dashboard/projects/TimeLine')));
const Reports = Loadable(lazy(() => import('../pages/dashboard/projects/Reports')));
const ProjectJobs = Loadable(lazy(() => import('../pages/dashboard/projects/Jobs')));
const ProjectTeam = Loadable(lazy(() => import('../pages/dashboard/projects/DefaultTeam')));
const QuickTemplate = Loadable(lazy(() => import('../pages/dashboard/projects/teamsBudgets/QuickTemplate/QuickTemplate')));
const DetailedTemplate = Loadable(lazy(() => import('../pages/dashboard/projects/teamsBudgets/DetailedTemplate/DetailedTemplate')));
// Teams
const TeamsQuick = Loadable(lazy(() => import('../pages/dashboard/projects/teamsProjects/QTemplate/QuickTemplate')));
const TeamsDetailed = Loadable(lazy(() => import('../pages/dashboard/projects/teamsProjects/DTemplate/DetailedTemplate')));

const TeamDashboard = Loadable(lazy(() => import('../pages/dashboard/projects/teamsProjects/TeamMainDashboard/TeamDashboard')));
const TeamPayment = Loadable(lazy(() => import('../pages/dashboard/projects/teamsProjects/TeamMainDashboard/TeamPayment')));
const TeamConnect = Loadable(lazy(() => import('../pages/dashboard/projects/teamsProjects/TeamMainDashboard/TeamConnect')));
const TeamReports = Loadable(lazy(() => import('../pages/dashboard/projects/teamsProjects/TeamMainDashboard/TeamReports')));
const TeamSetting = Loadable(lazy(() => import('../pages/dashboard/projects/teamsProjects/TeamMainDashboard/TeamSetting')));


const Remuneration = Loadable(lazy(() => import('../pages/dashboard/projects/Remuneration')));
const RemunerationDetailed = Loadable(lazy(() => import('../pages/dashboard/projects/Remuneration/RemunerationDetailed')));
const RemunerationInvoice = Loadable(lazy(() => import('../pages/dashboard/projects/Remuneration/Invoice')));




const Teams = Loadable(lazy(() => import('../pages/dashboard/projects/Teams')));
const CreateProject = Loadable(lazy(() => import('../pages/dashboard/projects/CreateProject')));
const Projects = Loadable(lazy(() => import('../pages/dashboard/projects/Projects')));
const TeamMember = Loadable(lazy(() => import('../pages/dashboard/projects/CreateTeam')));

const ViewJobs = Loadable(lazy(() => import('../pages/dashboard/ViewJobs')));
const ViewJobDashboard = Loadable(lazy(() => import('../pages/dashboard/ViewJobDashboard')));
const ViewCandidateList = Loadable(lazy(() => import('../pages/dashboard/ViewCandidateList')));
const JobDashboardMessage = Loadable(lazy(() => import('../pages/dashboard/JobMessage')));
const ViewCandidate = Loadable(lazy(() => import('../pages/dashboard/ViewCandidate')));
const Interviews = Loadable(lazy(() => import('../pages/dashboard/Interviews')));
const JobseekView = Loadable(lazy(() => import('../pages/dashboard/JobseekView')));
const JobReport = Loadable(lazy(() => import('../pages/dashboard/JobReport')));
const JobTimeline = Loadable(lazy(() => import('../pages/dashboard/JobTimeline')));

